// src/views/QuoteDetailsView.js
// PHASE 4.5 VERIFIER: r1-p45-qdv-7b3e9a14
//
// Operator-side quote viewer with selective-approval awareness.
//
// Phase 4 delivered the customer-side portal UI that lets customers
// approve some line items and decline others. This view is the
// operator counterpart — when a customer comes back with a partial
// approval, the operator now sees:
//   - Status badge in the header (Approved / Declined / ChangesRequested)
//   - Declined items dimmed with a "Declined by customer" tag
//   - Approved total prominent, original total shown beneath it
//   - Customer's change-request note rendered prominently when present
//   - Included items rendered nested under their parent
//
// Behavior is fully backward compatible — quotes without the new
// approval fields (legacy or simply not yet approved) render exactly
// as before.

import React, { useState, useMemo, useEffect, useRef } from 'react';
import LineDescription from '../components/documents/LineDescription'; // linetext-4d8b2e61 — line descriptions keep line breaks, indents and bullets
import { LINE_NAME, LINE_DESC, LINE_NUM } from '../components/documents/lineItemStyles'; // linetext-4d8b2e61 — the PDF's line layout
import { functions, auth } from '../firebase/config';
import { httpsCallable } from 'firebase/functions';
import {
    ChevronLeft, Printer, Send, Edit, FileText, Loader2, Briefcase,
    Check, X, MessageSquare, AlertCircle, Lock, Link2, Star, Plus, Download, ExternalLink,
    ChevronDown, ChevronUp, Fingerprint, RotateCcw, Clock, Eye
} from 'lucide-react';
import { formatDate, formatCurrency, toJsDate } from '../utils/formatters'; // qdvdate-3f7a1c9e — coerce stored Firestore Timestamp before formatDate so the operator reads date/expiry the way the portal already does
import {
    SELECTION_TYPES,
    isApprovedStatus,
    isDeclinedStatus,
    isChangesRequestedStatus,
    DOC_TYPES,
    resolveDocTerminology,
    // SEAM 6 VERIFIER: r1-dep-qdv-import-2b8f4e37
    QUOTE_STATUSES,
    DEPOSIT_STATUSES,
    // 1c-qdv-import-8a1c5e7f — Seam 1c: offline record methods for the record modal
    DEPOSIT_OFFLINE_METHODS,
    normalizeStatus,
    // qexp-6f2a9d38-qdv — Seam 5: derived expiry state
    EXPIRY_STATES,
    resolveExpiryState,
    resolveExpiryWarningDays,
    // depdraft-3c8e5a14 — "Collect a deposit" ON with no amount yet
    isDepositAmountNeeded,
    DEPOSIT_AMOUNT_NEEDED_MESSAGE,
} from '../utils/quoteSchema'; // r1-p1-docterm-qdv-c3f8b7a1
import { isQuoteArchived } from '../utils/quoteSchema'; // estdel-f9ac8ba8
import { DeleteQuoteButton, QuoteDeletedBanner, QuoteRestoredNotice } from '../components/crm/QuoteDeleteControls'; // estdel-f9ac8ba8
// r1-1a-notes-imports-4a7f2e93 — Seam 1a: Notes & Media on Quote Detail
import useOrgNotes from '../hooks/useOrgNotes';
import NoteComposer from '../components/crm/NoteComposer';
import NoteCard from '../components/crm/NoteCard';
import QuoteActivityTimeline from '../components/crm/QuoteActivityTimeline'; // qact-3b7e9d52-qdv-import
import QuotePipelineStagePicker from '../components/crm/QuotePipelineStagePicker'; // qpipe-4d1a7c35-qdv-import
import usePipelineStages from '../hooks/usePipelineStages'; // qpipe-4d1a7c35-qdv-import
import QuoteFollowUpStrip from '../components/crm/QuoteFollowUpStrip'; // qfup-7d3e9a41-qdv-import
import QuoteAttentionStrip from '../components/crm/QuoteAttentionStrip'; // qattn-8c4e1f92-qdv-import
import useQuoteVocabularies from '../hooks/useQuoteVocabularies'; // qfup-7d3e9a41-qdv-import
import useQuoteActivity from '../hooks/useQuoteActivity'; // qfup-7d3e9a41-qdv-import — ONE shared activity subscription for the strip + the timeline
// qclose-3e8a5c17-qdv-import — Seam 9b: close summary / nudge + the close modal
import useQuoteClose, { closeNeedsAnswer } from '../hooks/useQuoteClose';
import QuoteCloseStrip from '../components/crm/QuoteCloseStrip';
import useQuoteView, { quoteViewSummary } from '../hooks/useQuoteView'; // qview-6b2d4e91
import CloseDetailsModal from '../components/crm/CloseDetailsModal';
import { can, PERMISSIONS } from '../utils/permissions';
import { hasApprovalReceipt, resolveApprovalBreakdown } from '../utils/approvalReceipt'; // s8receipt-2f6d9a41; breakdown: s8totals-6a1c3e85
import { downloadPdfBytes, printPdfBytes } from '../utils/pdfDownload'; // docpdf-8a4c1e73
import SentDocumentCopies from '../components/common/SentDocumentCopies'; // docpdf-8a4c1e73
// 1c-qdv-import-8a1c5e80 — Seam 1c: reused confirm modal for record/waive deposit
import ConfirmationModal from '../components/common/ConfirmationModal';
// sdrefund3-dep-9d1f4c7a — Seam D: shared refund form for a paid/recorded deposit
import RefundModal from '../components/common/RefundModal';
import TaxExemptionBanner, { withExemptionRefresh } from '../components/common/TaxExemptionBanner'; // taxexempt3-8c4f2a1d — withExemptionRefresh: the server's new figures right after a switch-off


// ────────────────────────────────────────────────────────────────────
// Pure helper: build a set of declined itemIds for fast lookup.
// Falls back gracefully when approvedItemIds/declinedItemIds aren't
// set (legacy or not-yet-approved quotes).
// ────────────────────────────────────────────────────────────────────
function buildDeclinedSet(quote) {
    if (!quote) return new Set();
    if (Array.isArray(quote.declinedItemIds)) return new Set(quote.declinedItemIds);
    return new Set();
}


// ────────────────────────────────────────────────────────────────────
// Pure helper: sort line items so each Included child renders right
// after its parent (matches PortalQuoteDetail's ordering for visual
// consistency).
// ────────────────────────────────────────────────────────────────────
function orderItemsForDisplay(lineItems) {
    if (!Array.isArray(lineItems)) return [];
    const nonIncluded = lineItems.filter(i => i.selectionType !== SELECTION_TYPES.INCLUDED);
    const included = lineItems.filter(i => i.selectionType === SELECTION_TYPES.INCLUDED);
    const ordered = [];
    const seen = new Set();
    for (const item of nonIncluded) {
        ordered.push(item);
        for (const child of included) {
            if (child.parentItemId === item.itemId && !seen.has(child.itemId)) {
                ordered.push(child);
                seen.add(child.itemId);
            }
        }
    }
    for (const child of included) if (!seen.has(child.itemId)) ordered.push(child);
    return ordered;
}


// r1-1a-notes-props-8c3d5f10 — Seam 1a adds orgId + currentUserProfile for org-scoped notes
// calreturn-7b2e9d41 — `backLabel` overrides the word after "Back to" when this view was
// opened from somewhere other than the list (today: the calendar). Optional and null by
// default, so every existing caller keeps the terminology-resolved label untouched.
// qclose-3e8a5c17 — `awaitClosePrompt` ({ quoteId, since }) is set by CrmApp when
// the edit form just saved a status that closes the estimate; this view opens the
// close question once the server's close record appears, then calls
// onClosePromptHandled. Both optional — every other caller is unchanged.
const QuoteDetailsView = ({ quote, companySettings, onBack, backLabel, onEditQuote, onConvertToInvoice, onConvertToJob, onViewJob, onReQuoteDeclined, isConverting, orgId, currentUserProfile, awaitClosePrompt = null, onClosePromptHandled }) => {
    const [isResending, setIsResending] = useState(false);
    const [resendMessage, setResendMessage] = useState('');
    // r1-p6-qdvpdfbtn-6b1e7d28 — operator quote-PDF download
    const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
    // r1-p6-qdvprintbtn-9d2f3a61 — operator quote-PDF print
    const [isPrintingPdf, setIsPrintingPdf] = useState(false);

    // 1c-qdv-state-6b3e9f2d — Seam 1c: manual deposit resolution (record / waive).
    // recordLoading/waiveLoading double as the in-flight guard (handlers early-return
    // while true) and the button loading label. depositError styles the feedback line.
    const [showRecordModal, setShowRecordModal] = useState(false);
    const [showWaiveModal, setShowWaiveModal] = useState(false);
    const [recordAmount, setRecordAmount] = useState('');
    const [recordMethod, setRecordMethod] = useState(DEPOSIT_OFFLINE_METHODS.ETRANSFER);
    const [recordLoading, setRecordLoading] = useState(false);
    const [waiveLoading, setWaiveLoading] = useState(false);
    const [depositMessage, setDepositMessage] = useState('');
    const [depositError, setDepositError] = useState(false);
    // sdrefund3-dep-9d1f4c7a — Seam D: refund a paid/recorded deposit (shared RefundModal).
    const [showRefundModal, setShowRefundModal] = useState(false);
    // d2retry-5e1b8f42 — Seam D2: operator retry for a QuickBooks reversal that failed/gave up.
    const [retryingReversal, setRetryingReversal] = useState(false);
    const [reversalRetryMessage, setReversalRetryMessage] = useState('');
    const [reversalRetryError, setReversalRetryError] = useState(false);

    // r1-p7-qdv-approval-effect-6b2f8d51 — Phase 7.1 + 7.5 operator approval receipt
    // Fetches the persisted approval/audit doc for an approved quote via the
    // getQuoteApprovalDetails callable (operator-auth, org resolved server-side).
    // Degrades silently to null when not approved / not found.
    const [approval, setApproval] = useState(null);
    const [approvalLoading, setApprovalLoading] = useState(false);
    const [approvalExpanded, setApprovalExpanded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        // s8receipt-2f6d9a41 — Approved OR AwaitingDeposit (was 'Approved' only, so a signed
        // estimate awaiting its deposit showed no card). Same rule as the server's.
        if (!hasApprovalReceipt({ status: quote.status, latestApprovalId: quote.latestApprovalId }) || !quote.id) {
            setApproval(null);
            return;
        }
        setApprovalLoading(true);
        const fn = httpsCallable(functions, 'getQuoteApprovalDetails');
        fn({ quoteId: quote.id })
            .then((res) => {
                if (cancelled) return;
                setApproval(res.data?.approval || null);
            })
            .catch((err) => {
                if (cancelled) return;
                console.warn('getQuoteApprovalDetails failed:', err);
                setApproval(null);
            })
            .finally(() => {
                if (!cancelled) setApprovalLoading(false);
            });
        return () => { cancelled = true; };
    }, [quote.id, quote.status, quote.latestApprovalId]);

    // ── Format approval duration "2m 14s" ──────────────────────────
    const formatApprovalDuration = (ms) => {
        if (ms == null || !Number.isFinite(ms) || ms < 0) return '';
        const totalSec = Math.round(ms / 1000);
        const m = Math.floor(totalSec / 60);
        const s = totalSec % 60;
        if (m > 0) return `${m}m ${s}s`;
        return `${s}s`;
    };

    const currencyCode = companySettings?.currency || 'USD';

    // r1-p1-docterm-qdv-c3f8b7a1
    // Per-doc word reads THIS document's own docType (freeze rule) — drives the body
    // header, "Quote #:", "Resend", and the approval/decline banners. Aggregate plural
    // reads the org default — drives only the "Back to <list>" nav link.
    const { word: docWord } = resolveDocTerminology(quote?.docType);
    const { wordPlural: aggWordPlural } = resolveDocTerminology(companySettings?.quoteTerminologyDefault || DOC_TYPES.QUOTE);

    // ── Status flags (canonical + legacy tolerant) ─────────────────
    const status = quote.status || '';
    const approved = isApprovedStatus(status);
    const declined = isDeclinedStatus(status);
    const changesRequested = isChangesRequestedStatus(status);
    const sent = status.toLowerCase() === 'sent';
    // qexp-6f2a9d38-qdv — Seam 5. This used to be `status.toLowerCase() === 'expired'`,
    // which could NEVER be true: no code path has ever written status 'Expired',
    // so the badge below was unreachable. It is now DERIVED from expiresAt by the
    // same rule the portal, the server guard and the warning cron use — and the
    // stored status stays 'Sent' underneath, which is the point of the seam.
    const expiryWarningDays = resolveExpiryWarningDays(companySettings?.quoteDefaults);
    const expiry = resolveExpiryState(quote, {
        timeZone: companySettings?.timezone,
        warningDays: expiryWarningDays,
    });
    const expired      = expiry.state === EXPIRY_STATES.EXPIRED;
    const expiringSoon = expiry.state === EXPIRY_STATES.EXPIRING_SOON;

    // SEAM 6 VERIFIER: r1-dep-qdv-state-9d3a6c48
    // Deposits Seam 6. 'AwaitingDeposit' satisfies none of the predicates
    // above, so before this seam the operator saw NO banner and NO convert
    // buttons on a signed-but-unpaid estimate, with nothing explaining why.
    // Hiding the convert buttons is correct (the deposit hasn't cleared);
    // the missing explanation was not.
    const awaitingDeposit = normalizeStatus(status) === QUOTE_STATUSES.AWAITING_DEPOSIT;
    const depositPaid     = quote.depositStatus === DEPOSIT_STATUSES.PAID
                         || quote.depositStatus === DEPOSIT_STATUSES.RECORDED;
    const depositAmount   = quote.deposit?.amountResolved;
    // 1c-qdv-unpaid-4d6f2b8c — Seam 1c gate. An unpaid deposit with a real amount,
    // regardless of quote.status — this serves BOTH populations: the stuck
    // AwaitingDeposit (required) quote and an already-Approved quote carrying an
    // unpaid optional deposit. The action row is nested inside the status-banner
    // block below, whose outer gate excludes the Draft/Sent artifact.
    const depositUnpaid   = quote.depositStatus === DEPOSIT_STATUSES.UNPAID
                         && Number(depositAmount) > 0;
    // 1c-qdv-recorded-2f9a6c1d — A RECORDED (offline) deposit must display what was
    // actually received, not the agreed amount. recordedAmount is the truth; it is
    // absent on older recorded deposits and never set on the Stripe-paid path, so
    // fall back to the agreed amount there. Compare numerically to spot a shortfall
    // or overage. depositAmount (the agreed amount) is left untouched — other
    // surfaces (awaiting-deposit line, record-modal prefill, waive confirm) depend on it.
    const depositRecorded       = quote.depositStatus === DEPOSIT_STATUSES.RECORDED;
    const depositReceivedAmount = quote.deposit?.recordedAmount != null
        ? quote.deposit.recordedAmount
        : depositAmount;
    const depositAmountsDiffer  = depositRecorded
                               && depositReceivedAmount != null
                               && depositAmount != null
                               && Number(depositReceivedAmount) !== Number(depositAmount);

    // d2warn-7d3c9a1e -- Seam D2: warn (never block) before refunding a deposit that reached
    // QuickBooks when no refund-from bank account is configured. qbPaymentId is the "reached
    // QuickBooks" signal (mirrors the backend reachedQb predicate); the org setting lives at
    // companySettings.quickbooksSettings.refundFromAccountId. Passed as a prop into the shared
    // RefundModal, so the invoice refund path (which never passes it) stays clean.
    const showRefundAccountWarning = !!quote.deposit?.qbPaymentId
                                  && !companySettings?.quickbooksSettings?.refundFromAccountId;

    // ── Was this a PARTIAL approval? ───────────────────────────────
    // True only when the quote is approved AND we have stored
    // approvedItemIds AND at least one item was declined.
    // s8totals-6a1c3e85 — Approved OR AwaitingDeposit (signed, deposit owed): a partially approved
    // estimate awaiting its deposit showed the ORIGINAL total (check predated AwaitingDeposit).
    // The portal already shows approved figures for both (isPostApproval).
    const { isPartialApproval } = resolveApprovalBreakdown(quote);
    const declinedSet = useMemo(() => buildDeclinedSet(quote), [quote]);

    // ── Compute totals for legacy display (when no approved* fields) ─
    // For modern quotes, we prefer the server-computed approved values.
    const { subtotal, taxAmount, total } = useMemo(() => {
        const lineItems = quote.lineItems || [];
        const subtotalCalc = lineItems.reduce(
            (acc, item) => acc + (Number(item.quantity || 0) * Number(item.price || 0)), 0
        );
        const taxableSubtotal = lineItems
            .filter(item => !item.taxExempt)
            .reduce((acc, item) => acc + (Number(item.quantity || 0) * Number(item.price || 0)), 0);
        const taxRate = companySettings?.taxRate || 0;
        const taxAmountCalc = taxableSubtotal * (taxRate / 100);
        const totalCalc = subtotalCalc + taxAmountCalc;
        return { subtotal: subtotalCalc, taxAmount: taxAmountCalc, total: totalCalc };
    }, [quote.lineItems, companySettings]);

    // ── Display amounts: prefer stored quote totals (server-truth) over recompute ─
    // The quote document already has subtotal/taxAmount/total from the time it
    // was sent. Trust those over a recompute, which would re-roll tax against
    // the current companySettings.taxRate (could have changed since send).
    const originalSubtotal = quote.subtotal != null ? Number(quote.subtotal) : subtotal;
    const originalTaxAmount = quote.taxAmount != null ? Number(quote.taxAmount) : taxAmount;
    const originalTotal = quote.total != null ? Number(quote.total) : total;

    const approvedSubtotal = quote.approvedSubtotal != null ? Number(quote.approvedSubtotal) : null;
    const approvedTaxAmount = quote.approvedTaxAmount != null ? Number(quote.approvedTaxAmount) : null;
    const approvedTotal = quote.approvedTotal != null ? Number(quote.approvedTotal) : null;

    const handleResend = async () => {
        if (!quote?.id) return;
        // depdraft-3c8e5a14 — refused before calling the server (which refuses it too)
        if (isDepositAmountNeeded(quote)) {
            setResendMessage(`Failed to send ${docWord.toLowerCase()}: ${DEPOSIT_AMOUNT_NEEDED_MESSAGE}`);
            setTimeout(() => setResendMessage(''), 5000);
            return;
        }
        setIsResending(true);
        setResendMessage('');
        try {
            if (!auth.currentUser) throw new Error('User is not authenticated.');
            const sendQuote = httpsCallable(functions, 'sendQuoteEmail');
            const result = await sendQuote({ quoteId: quote.id });
            if (result.data.success) {
                setResendMessage(`${docWord} sent successfully!`);
            } else {
                setResendMessage(`Failed to send ${docWord.toLowerCase()}: ${result.data.error || 'An unknown server error occurred.'}`);
            }
            setTimeout(() => setResendMessage(''), 5000);
        } catch (error) {
            console.error("Detailed error:", error);
            setResendMessage(`Failed to send ${docWord.toLowerCase()}: ${error.message}`);
        }
        setIsResending(false);
    };

    // 1c-qdv-handlers-1c7a4e93 — Seam 1c. Two operator deposit-resolution calls,
    // shaped like handleResend: a per-action loading flag that also guards against
    // double-fire, a single httpsCallable, try/catch, readable feedback string.
    // The server is the real gate; the client validation below is only for usability.
    const recordCap = approvedTotal != null ? approvedTotal : (originalTotal || 0);
    const handleRecordDeposit = async () => {
        if (recordLoading) return; // in-flight guard: no double-fire
        if (!quote?.id) return;
        const amtNum = Math.round(Number(recordAmount) * 100) / 100;
        if (!Number.isFinite(amtNum) || amtNum <= 0 || (recordCap > 0 && amtNum > recordCap)) {
            setDepositError(true);
            setDepositMessage(`Enter an amount greater than zero and no more than the ${docWord.toLowerCase()} total.`);
            return;
        }
        setRecordLoading(true);
        setDepositError(false);
        setDepositMessage('');
        try {
            if (!auth.currentUser) throw new Error('User is not authenticated.');
            const recordDeposit = httpsCallable(functions, 'recordDeposit');
            await recordDeposit({ quoteId: quote.id, amount: amtNum, method: recordMethod });
            setShowRecordModal(false);
            setDepositError(false);
            setDepositMessage('Deposit recorded.');
            setTimeout(() => setDepositMessage(''), 5000);
        } catch (error) {
            console.error('recordDeposit failed:', error);
            setDepositError(true);
            setDepositMessage(error?.message || 'Could not record the deposit. Please try again.');
        }
        setRecordLoading(false);
    };

    const handleWaiveDeposit = async () => {
        if (waiveLoading) return; // in-flight guard: no double-fire
        if (!quote?.id) return;
        setWaiveLoading(true);
        setDepositError(false);
        setDepositMessage('');
        try {
            if (!auth.currentUser) throw new Error('User is not authenticated.');
            const waiveDeposit = httpsCallable(functions, 'waiveDeposit');
            await waiveDeposit({ quoteId: quote.id });
            setShowWaiveModal(false);
            setDepositError(false);
            setDepositMessage('Deposit waived.');
            setTimeout(() => setDepositMessage(''), 5000);
        } catch (error) {
            console.error('waiveDeposit failed:', error);
            setDepositError(true);
            setDepositMessage(error?.message || 'Could not waive the deposit. Please try again.');
        }
        setWaiveLoading(false);
    };

    // sdrefund3-dep-9d1f4c7a — refund a paid/recorded deposit. The RefundModal owns
    // the amount/method/cap; here we call the role-gated callable and refresh so the
    // deposit line flips to "refunded" and any reopened invoice balance shows. The
    // callable throws on failure, which the modal surfaces inline.
    const handleRefundDeposit = async ({ amount, offline, method }) => {
        const refund = httpsCallable(functions, 'refundDeposit');
        await refund({ quoteId: quote.id, amount, offline, method });
        window.location.reload();
    };

    // d2retry-5e1b8f42 — Seam D2: ask the backend to re-arm and immediately re-attempt a
    // failed QuickBooks deposit reversal (retryDepositReversal, role-gated server-side). On
    // success we reload so the deposit line reflects the new state; on failure we surface the
    // server's message so the operator knows what happened rather than guessing.
    const handleRetryReversal = async () => {
        if (retryingReversal) return;
        setRetryingReversal(true);
        setReversalRetryError(false);
        setReversalRetryMessage('');
        try {
            const retry = httpsCallable(functions, 'retryDepositReversal');
            await retry({ quoteId: quote.id });
            setReversalRetryMessage('Retrying the QuickBooks reversal… refreshing.');
            window.location.reload();
        } catch (error) {
            setReversalRetryError(true);
            setReversalRetryMessage(error?.message || 'Could not retry the QuickBooks reversal. Please try again.');
            setRetryingReversal(false);
        }
    };

    // docpdf-8a4c1e73 — getQuotePdf returns the PDF's bytes (nothing stored, no link; was downloadQuotePdf
    // + a 7-day Storage link). Download saves the file; Print loads it into a hidden frame on this page.
    const fetchQuotePdf = async () => {
        if (!auth.currentUser) throw new Error('User is not authenticated.');
        const result = await httpsCallable(functions, 'getQuotePdf', { timeout: 120000 })({ quoteId: quote.id });
        return result.data || {};
    };
    const handleDownloadPdf = async () => {
        if (!quote?.id) return;
        setIsDownloadingPdf(true);
        setResendMessage('');
        try {
            const { pdfBase64, fileName } = await fetchQuotePdf();
            downloadPdfBytes(pdfBase64, fileName);
        } catch (error) {
            console.error('Quote PDF download failed:', error);
            setResendMessage(`Failed to download PDF: ${error.message}`);
            setTimeout(() => setResendMessage(''), 5000);
        }
        setIsDownloadingPdf(false);
    };

    const handlePrintPdf = async () => {
        if (!quote?.id) return;
        setIsPrintingPdf(true);
        setResendMessage('');
        try {
            const { pdfBase64, fileName } = await fetchQuotePdf();
            printPdfBytes(pdfBase64, fileName);
        } catch (error) {
            console.error('Quote PDF print failed:', error);
            setResendMessage(`Failed to print PDF: ${error.message}`);
            setTimeout(() => setResendMessage(''), 5000);
        }
        setIsPrintingPdf(false);
    };

    const customer = quote.customerDetails || { name: quote.customerName, email: quote.customerEmail };
    // seamE-4a7c1e58 — the dead `hasBeenConverted` guard (read `quote.linkedInvoiceId`,
    // a field nothing writes, so always false) is removed. Duplicate-conversion
    // protection now lives in handleConvertToInvoice's confirmation gate (Seam E).
    // CONVERT-FIX VERIFIER: r1-cvt-qdv-convertedjob-9a4c2f81
    // Choice A: a quote converted to a job is tracked solely by
    // convertedToJobId (status stays Approved). When present, the
    // "Convert to Job" button is replaced by "View Job".
    const convertedJobId = quote.convertedToJobId || null;

    // r1-1a-notes-state-2e9b4c76 — Seam 1a: collapsed Notes & Media strip.
    // notesExpanded: strip collapsed by default. composerDirty is fed by
    // NoteComposer.onDirtyChange so we can warn on exit with unsaved content.
    const [notesExpanded, setNotesExpanded] = useState(false);
    const [composerDirty, setComposerDirty] = useState(false);
    const notesAttachedKey = quote?.id ? `quote:${quote.id}` : null;
    // qact-3b7e9d52-qdv-notes — Seam 3: `loading` is also handed to the Activity
    // timeline so it only calls a note_added row "deleted" once notes have
    // loaded; the ref + handler let a timeline row open and scroll to this strip.
    const { notes: quoteNotes, loading: quoteNotesLoading } = useOrgNotes({ orgId, attachedKey: notesAttachedKey });
    // qpipe-4d1a7c35-qdv-stages — Seam 4: the org's stage list, for the internal
    // stage strip and for stage_change labels in the timeline. null without orgId.
    const pipelineStages = usePipelineStages(orgId);
    const quoteVocabularies = useQuoteVocabularies(orgId); // qfup-7d3e9a41-qdv-hook
    // qfup-7d3e9a41-qdv-hook — subscribed ONCE here and shared; two components
    // each opening their own listener would bill the same 50 docs twice.
    const { entries: activityEntries, loadError: activityLoadError } = useQuoteActivity(orgId, quote?.id || null);

    // qclose-3e8a5c17-qdv-close — Seam 9b. ONE live close record and ONE modal for
    // the page; the strip, the stage picker and the post-save prompt all open the
    // same dialog, so the page never shows two.
    const { record: closeRecord } = useQuoteClose(orgId, quote?.id || null);
    // qview-6b2d4e91 — Seam 10: when the customer looked at it in the portal.
    const { record: viewRecord } = useQuoteView(orgId, quote?.id || null);
    const viewSummary = quoteViewSummary(viewRecord);
    const canAnswerClose = can(currentUserProfile, PERMISSIONS.MANAGE_FINANCIALS);
    const [closePrompt, setClosePrompt] = useState(null); // { closeActivityId, outcome } | null
    const openClosePrompt = (closeActivityId = null, outcome = null) =>
        setClosePrompt({ closeActivityId, outcome });

    // After the edit form saved a closing status: the close record is written by
    // the onQuotePipelineSync TRIGGER, a few seconds AFTER the save, so wait for
    // it. Only a record closed at/after the save counts (2-minute allowance for
    // the browser's clock vs the server's); nothing appearing within 90 seconds
    // means this save did not close anything we record (e.g. a NEW estimate saved
    // straight as Approved has no stage it closed from) — then just stop waiting.
    const awaitingHere = !!awaitClosePrompt && awaitClosePrompt.quoteId === quote?.id;
    useEffect(() => {
        if (!awaitingHere) return undefined;
        const t = setTimeout(() => onClosePromptHandled && onClosePromptHandled(), 90 * 1000);
        return () => clearTimeout(t);
    }, [awaitingHere]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!awaitingHere || !closeRecord || !canAnswerClose) return;
        const closedMs = closeRecord.closedAt && typeof closeRecord.closedAt.toMillis === 'function'
            ? closeRecord.closedAt.toMillis() : null;
        if (closedMs === null || closedMs < awaitClosePrompt.since - 2 * 60 * 1000) return;
        if (closeNeedsAnswer(closeRecord)) openClosePrompt(closeRecord.closeActivityId, closeRecord.outcome);
        if (onClosePromptHandled) onClosePromptHandled();
    }, [awaitingHere, closeRecord, canAnswerClose]); // eslint-disable-line react-hooks/exhaustive-deps
    const notesStripRef = useRef(null);
    const openNotesStrip = () => {
        setNotesExpanded(true);
        requestAnimationFrame(() => notesStripRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    };

    // Warn on browser navigation/refresh/close while the composer holds unsaved
    // content. In-app "Back" is guarded separately at the Back button onClick.
    useEffect(() => {
        if (!composerDirty) return undefined;
        const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [composerDirty]);

    const orderedItems = useMemo(() => orderItemsForDisplay(quote.lineItems || []), [quote.lineItems]);

    // estdel-f9ac8ba8 — a deleted (hidden) estimate is read-only: only Print and Download PDF stay, plus
    // Restore on the banner for an Owner / Admin. restoredId = the estimate just restored (green notice).
    const isDeleted = isQuoteArchived(quote);
    const [restoredId, setRestoredId] = useState(null);

    return (
        <div className="p-4 sm:p-6 md:p-10">
            {/* Header Actions - Stack on mobile, row on desktop */}
            <div className="flex flex-col gap-4 mb-6">
                {/* taxexempt3-8c4f2a1d — the customer's tax exemption on this estimate (staff only) */}
                <TaxExemptionBanner kind="quote" document={quote} orgId={orgId || currentUserProfile?.organizationId} currentUserProfile={currentUserProfile} />
                {/* r1-1a-notes-backguard-6d1a9f42 — warn before leaving with unsaved note content */}
                <button onClick={() => { if (composerDirty && !window.confirm('You have an unsaved note (text or photos). Leave without saving?')) return; onBack(); }} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 w-fit">
                    {/* calreturn-7b2e9d41 — backLabel wins when the caller supplied one;
                        otherwise this stays the org-default plural, so the terminology
                        freeze rule for aggregate surfaces is untouched. */}
                    <ChevronLeft size={20} /> Back to {backLabel || aggWordPlural}
                </button>

                <div className="flex flex-wrap gap-2">
                    {approved && !isDeleted && (
                        <>
                            {/* CONVERT-FIX VERIFIER: r1-cvt-qdv-buttongate-5d8f3b27 */}
                            {convertedJobId ? (
                                <button
                                    onClick={() => onViewJob && onViewJob(quote)}
                                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 min-w-[140px]"
                                >
                                    <ExternalLink size={16} /> View Job
                                </button>
                            ) : (
                                <button
                                    onClick={() => onConvertToJob(quote)}
                                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 min-w-[140px]"
                                >
                                    <Briefcase size={16} /> Convert to Job
                                </button>
                            )}

                            {/* seamE-4a7c1e58 — plain, always-enabled Convert to Invoice.
                                isConverting stays: it's a real spinner + re-entrancy state
                                now that the handler runs an async detection query. */}
                            <button
                                onClick={() => onConvertToInvoice(quote)}
                                disabled={isConverting}
                                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed min-w-[140px]"
                            >
                                {isConverting ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
                                {isConverting ? 'Converting...' : 'Convert to Invoice'}
                            </button>

                            {/* r1-p7-requote-btn-9f2c4e85 — Phase 7.2 Re-quote declined items.
                                Shown only on a genuine partial approval (some approved, some
                                declined). Creates a NEW Draft quote from just the declined lines. */}
                            {isPartialApproval && onReQuoteDeclined && (
                                <button
                                    onClick={() => onReQuoteDeclined(quote)}
                                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 min-w-[140px]"
                                >
                                    <Plus size={16} /> Re-quote Declined
                                </button>
                            )}
                        </>
                    )}
                    {!isDeleted && ( // estdel-f9ac8ba8
                    <button onClick={() => onEditQuote(quote)} className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                        <Edit size={16} /> Edit
                    </button>
                    )}
                    <button onClick={handlePrintPdf} disabled={isPrintingPdf} className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed">
                        {isPrintingPdf ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />}
                        {isPrintingPdf ? 'Preparing...' : 'Print'}
                    </button>
                    <button onClick={handleDownloadPdf} disabled={isDownloadingPdf} className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed">
                        {isDownloadingPdf ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                        {isDownloadingPdf ? 'Generating...' : 'Download PDF'}
                    </button>
                    {!isDeleted && ( // estdel-f9ac8ba8
                    <button onClick={handleResend} disabled={isResending} className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400">
                        <Send size={16} /> {isResending ? 'Sending...' : `Resend ${docWord}`}
                    </button>
                    )}
                    {/* estdel-f9ac8ba8 — last in the row; shown only when this estimate may be deleted by this person */}
                    <DeleteQuoteButton quote={quote} currentUserProfile={currentUserProfile} />
                </div>
            </div>

            {/* estdel-f9ac8ba8 — deleted: who and when, Restore for an Owner / Admin; then the green line once restored */}
            <QuoteDeletedBanner quote={quote} currentUserProfile={currentUserProfile} onRestored={(q) => setRestoredId(q.id)} />
            {!isDeleted && restoredId === quote.id && <QuoteRestoredNotice quote={quote} onDismiss={() => setRestoredId(null)} />}

            {resendMessage && <p className={`text-sm text-right mb-4 ${resendMessage.includes('Failed') ? 'text-red-500' : 'text-green-600'}`}>{resendMessage}</p>}

            {/* docpdf-8a4c1e73 — the PDFs Spatchy kept of this estimate's emails sent with the PDF attached (Office Staff and up; nothing for anyone else) */}
            <div className="max-w-4xl mx-auto flex justify-end"><SentDocumentCopies orgId={orgId} kind="estimate" docId={quote?.id} /></div>

            {/* depdraft-3c8e5a14 — saved with "Collect a deposit" ON and no amount yet (Draft or Sent) */}
            {isDepositAmountNeeded(quote) && (
                <div className="max-w-4xl mx-auto mb-4">
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                        <div className="flex items-start gap-3">
                            <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm font-semibold text-amber-900">Deposit amount needed</p>
                                <p className="text-sm mt-0.5 text-amber-800">
                                    This {docWord.toLowerCase()} collects a deposit, but no amount has been entered yet. Edit it and enter the amount before sending.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ───────── STATUS BANNER (customer response) ───────── */}
            {/* Phase 4.5 adds a prominent banner above the quote body so operators
                immediately see what the customer did. This was the bug Marcus
                caught — the operator couldn't tell at a glance that a partial
                approval had landed. */}
            {(approved || declined || changesRequested || awaitingDeposit) && (
                <div className="max-w-4xl mx-auto mb-4">
                    {/* SEAM 6 VERIFIER: r1-dep-qdv-banner-6e1c8b52
                        The customer HAS signed — this is not a "no response yet"
                        state. Says so, and explains why there are no convert
                        buttons: approval is held until the deposit clears. */}
                    {awaitingDeposit && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                            <div className="flex items-start gap-3">
                                <Lock size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <p className="text-sm font-semibold text-amber-900">
                                        Awaiting deposit{depositAmount != null ? ` of ${formatCurrency(depositAmount, currencyCode)}` : ''}
                                    </p>
                                    <p className="text-sm mt-0.5 text-amber-800">
                                        The customer signed this {docWord.toLowerCase()}, but a deposit is required before it's approved.
                                        It stays on hold — and no jobs are scheduled — until the payment clears.
                                    </p>
                                    {/* s8totals-6a1c3e85 — the amount they signed for (same line as the Approved banner). */}
                                    {approvedTotal != null && (
                                        <p className="text-sm mt-1 text-amber-800">
                                            {isPartialApproval
                                                ? <>Customer approved {quote.approvedItemIds.length} of {quote.approvedItemIds.length + quote.declinedItemIds.length} items — <strong>{formatCurrency(approvedTotal, currencyCode)}</strong>{originalTotal > 0 && <span className="text-gray-600"> (originally quoted {formatCurrency(originalTotal, currencyCode)})</span>}</>
                                                : <>Customer approved <strong>{formatCurrency(approvedTotal, currencyCode)}</strong></>}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    {approved && (
                        <div className={`rounded-lg border p-4 ${isPartialApproval ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                            <div className="flex items-start gap-3">
                                <Check size={18} className={`flex-shrink-0 mt-0.5 ${isPartialApproval ? 'text-amber-600' : 'text-green-600'}`} />
                                <div className="flex-1">
                                    <p className={`text-sm font-semibold ${isPartialApproval ? 'text-amber-900' : 'text-green-900'}`}>
                                        {isPartialApproval
                                            ? `Customer approved ${quote.approvedItemIds.length} of ${(quote.approvedItemIds.length + quote.declinedItemIds.length)} items`
                                            : `${docWord} approved by customer`
                                        }
                                    </p>
                                    {approvedTotal != null && (
                                        <p className={`text-sm mt-0.5 ${isPartialApproval ? 'text-amber-800' : 'text-green-800'}`}>
                                            Customer approved <strong>{formatCurrency(approvedTotal, currencyCode)}</strong>
                                            {isPartialApproval && originalTotal > 0 && (
                                                <span className="text-gray-600"> (originally quoted {formatCurrency(originalTotal, currencyCode)})</span>
                                            )}
                                        </p>
                                    )}
                                    {/* SEAM 6 VERIFIER: r1-dep-qdv-depline-3f7b9d64
                                        Optional-deposit case: approved on signature, deposit tracked
                                        alongside. Only rendered once a deposit actually exists —
                                        depositStatus is absent on every non-deposit estimate. */}
                                    {quote.depositStatus && (
                                        /* 1c-qdv-label-3e8f2a6b — Seam 1c: 'waived' reads as a
                                           neutral settled state (never the green "paid" treatment,
                                           never the amber "not paid yet"). */
                                        /* sdrefund3-depcol-2a5e8b16 — a refunded deposit reads warm/
                                           neutral (orange), never amber "not paid yet" (it isn't owing). */
                                        <p className={`text-sm mt-1 font-medium ${depositPaid ? 'text-green-800' : quote.depositStatus === DEPOSIT_STATUSES.WAIVED ? 'text-gray-700' : quote.depositStatus === DEPOSIT_STATUSES.REFUNDED ? 'text-orange-700' : 'text-amber-800'}`}>
                                            {/* 1c-qdv-recorded-2f9a6c1d — recorded shows the received amount; every other status uses the agreed amount as before */}
                                            Deposit{(depositRecorded ? depositReceivedAmount : depositAmount) != null ? ` of ${formatCurrency(depositRecorded ? depositReceivedAmount : depositAmount, currencyCode)}` : ''}
                                            {quote.depositStatus === DEPOSIT_STATUSES.PAID     ? ' paid'
                                             : quote.depositStatus === DEPOSIT_STATUSES.RECORDED ? ' recorded'
                                             : quote.depositStatus === DEPOSIT_STATUSES.REFUNDED ? ' refunded'
                                             : quote.depositStatus === DEPOSIT_STATUSES.WAIVED   ? ' waived'
                                             : ' not paid yet'}
                                            {depositAmountsDiffer ? ` (of ${formatCurrency(depositAmount, currencyCode)} agreed)` : ''}
                                        </p>
                                    )}
                                    {/* CONVERT-FIX: r1-cvt-qdv-bannerline-2b6e8a53 — Choice A marker, not a status */}
                                    {convertedJobId && (
                                        <p className="text-sm mt-1 font-medium text-purple-800">
                                            Converted to a job
                                            {onViewJob && (
                                                <button
                                                    onClick={() => onViewJob(quote)}
                                                    className="ml-2 underline text-purple-700 hover:text-purple-900"
                                                >
                                                    View Job
                                                </button>
                                            )}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    {declined && (
                        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                            <div className="flex items-start gap-3">
                                <X size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                                <p className="text-sm font-semibold text-red-900">{docWord} declined by customer</p>
                            </div>
                        </div>
                    )}
                    {changesRequested && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                            <div className="flex items-start gap-3">
                                <MessageSquare size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <p className="text-sm font-semibold text-amber-900">Customer requested changes</p>
                                    {quote.customerChangesNote && (
                                        <div className="mt-2 pt-2 border-t border-amber-200">
                                            <p className="text-xs font-bold text-amber-700 uppercase mb-1">Their note:</p>
                                            <p className="text-sm text-amber-900 italic whitespace-pre-wrap">"{quote.customerChangesNote}"</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    {/* 1c-qdv-actionrow-5f9c1d84 — Seam 1c: resolve an unpaid deposit offline.
                        Nested inside the status-banner block, so it inherits that block's outer
                        gate (approved/declined/changesRequested/awaitingDeposit) — which excludes
                        the console-only Draft/Sent artifact — and is further gated on depositUnpaid.
                        Serves both populations: a stuck AwaitingDeposit quote and an already-Approved
                        quote carrying an unpaid optional deposit. */}
                    {depositUnpaid && (
                        <>
                            <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                                <p className="text-sm font-semibold text-gray-800">This deposit hasn't been collected online.</p>
                                <p className="text-sm text-gray-600 mt-0.5">Record a payment you received offline, or waive it to proceed without collecting.</p>
                                <div className="flex flex-wrap gap-2 mt-3">
                                    <button
                                        onClick={() => {
                                            setDepositError(false);
                                            setDepositMessage('');
                                            setRecordAmount(depositAmount != null ? String(depositAmount) : '');
                                            setRecordMethod(DEPOSIT_OFFLINE_METHODS.ETRANSFER);
                                            setShowRecordModal(true);
                                        }}
                                        disabled={recordLoading || waiveLoading}
                                        className="flex items-center justify-center gap-2 px-4 py-2 bg-[#5F4EC9] text-white rounded-md hover:opacity-90 disabled:bg-gray-400"
                                    >
                                        {recordLoading ? 'Recording…' : 'Record deposit received'}
                                    </button>
                                    <button
                                        onClick={() => {
                                            setDepositError(false);
                                            setDepositMessage('');
                                            setShowWaiveModal(true);
                                        }}
                                        disabled={recordLoading || waiveLoading}
                                        className="flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 disabled:opacity-50"
                                    >
                                        {waiveLoading ? 'Waiving…' : 'Waive deposit'}
                                    </button>
                                </div>
                                {depositMessage && (
                                    <p className={`text-sm mt-2 ${depositError ? 'text-red-500' : 'text-green-600'}`}>{depositMessage}</p>
                                )}
                            </div>

                            <ConfirmationModal
                                isOpen={showRecordModal}
                                onClose={() => { if (!recordLoading) setShowRecordModal(false); }}
                                onConfirm={handleRecordDeposit}
                                title="Record deposit received"
                                message={`Confirm you've received this deposit outside of online payment. It's credited to the customer's invoice, just like an online payment.`}
                                confirmText={recordLoading ? 'Recording…' : 'Record deposit'}
                            >
                                <div className="space-y-3 mb-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Amount received</label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={recordAmount}
                                            onChange={(e) => setRecordAmount(e.target.value)}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5F4EC9]"
                                        />
                                        {(() => {
                                            const amtNum = Number(recordAmount);
                                            const invalid = !Number.isFinite(amtNum) || amtNum <= 0 || (recordCap > 0 && amtNum > recordCap);
                                            return invalid ? (
                                                <p className="text-xs text-red-500 mt-1">
                                                    Enter an amount greater than zero and no more than the {docWord.toLowerCase()} total
                                                    {recordCap > 0 ? ` (${formatCurrency(recordCap, currencyCode)})` : ''}.
                                                </p>
                                            ) : null;
                                        })()}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">How was it paid?</label>
                                        <select
                                            value={recordMethod}
                                            onChange={(e) => setRecordMethod(e.target.value)}
                                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5F4EC9]"
                                        >
                                            <option value={DEPOSIT_OFFLINE_METHODS.ETRANSFER}>E-transfer</option>
                                            <option value={DEPOSIT_OFFLINE_METHODS.CHEQUE}>Cheque</option>
                                            <option value={DEPOSIT_OFFLINE_METHODS.CASH}>Cash</option>
                                            <option value={DEPOSIT_OFFLINE_METHODS.OTHER}>Other</option>
                                        </select>
                                    </div>
                                </div>
                            </ConfirmationModal>

                            <ConfirmationModal
                                isOpen={showWaiveModal}
                                onClose={() => { if (!waiveLoading) setShowWaiveModal(false); }}
                                onConfirm={handleWaiveDeposit}
                                title="Waive deposit"
                                message={`Proceed without collecting the ${depositAmount != null ? `${formatCurrency(depositAmount, currencyCode)} ` : ''}deposit. No payment is recorded and nothing is credited to the invoice.${awaitingDeposit ? ` The ${docWord.toLowerCase()} moves to Approved.` : ''}`}
                                confirmText={waiveLoading ? 'Waiving…' : 'Waive deposit'}
                            />
                        </>
                    )}

                    {/* sdrefund3-dep-9d1f4c7a — Seam D: refund a deposit that was actually
                        collected. Shown ONLY for a paid/recorded deposit (depositPaid),
                        so it never appears for a waived deposit or one never paid. Opens
                        the same shared RefundModal wired to refundDeposit. */}
                    {depositPaid && (
                        <>
                            <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                                <p className="text-sm font-semibold text-gray-800">Need to return this deposit?</p>
                                <p className="text-sm text-gray-600 mt-0.5">Refund the deposit you collected. If it was credited to an invoice, that invoice's balance reopens.</p>
                                <div className="mt-3">
                                    <button
                                        onClick={() => { setDepositError(false); setDepositMessage(''); setShowRefundModal(true); }}
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-orange-300 text-orange-700 rounded-md hover:bg-orange-50"
                                    >
                                        <RotateCcw size={16}/> Refund deposit
                                    </button>
                                </div>
                                {depositMessage && (
                                    <p className={`text-sm mt-2 ${depositError ? 'text-red-500' : 'text-green-600'}`}>{depositMessage}</p>
                                )}
                            </div>

                            <RefundModal
                                isOpen={showRefundModal}
                                onClose={() => setShowRefundModal(false)}
                                title="Refund deposit"
                                subjectLabel={`Refund the deposit${depositReceivedAmount != null ? ` of ${formatCurrency(depositReceivedAmount, currencyCode)}` : ''} on ${docWord.toLowerCase()} #${quote.quoteNumber || ''}.`}
                                refundableAmount={depositReceivedAmount}
                                currencyCode={currencyCode}
                                hasCardPayment={!!quote.deposit?.stripePaymentIntentId}
                                warning={showRefundAccountWarning /* a6f2c3d7 — Seam D2: only the QB-synced-no-account case; blank otherwise */
                                    ? "This deposit was synced to QuickBooks, but no refund-from bank account is set. You can still refund now — the customer gets their money back — but QuickBooks won't reverse it automatically until you choose an account in Settings → Integrations."
                                    : ''}
                                onSubmit={handleRefundDeposit}
                            />
                        </>
                    )}

                    {/* d2retry-b8d4e91a — Seam D2: operator retry for a failed QuickBooks reversal.
                        Gated PURELY on qbReversalState === 'error' (not depositPaid) because the
                        reversal fails AFTER a refund, when depositStatus is 'refunded'. Shows only
                        in the failed state, and reports what happened rather than leaving the
                        operator guessing. This is the first frontend reader of qbReversalState. */}
                    {quote.deposit?.qbReversalState === 'error' && (
                        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                            <p className="text-sm font-semibold text-amber-900">QuickBooks reversal didn't finish</p>
                            <p className="text-sm text-amber-800 mt-0.5">
                                The refund went through, but QuickBooks couldn't reverse the deposit automatically. If you've set a refund-from bank account in Settings → Integrations, you can ask it to try again.
                            </p>
                            <div className="mt-3">
                                <button
                                    onClick={handleRetryReversal}
                                    disabled={retryingReversal}
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-amber-300 text-amber-800 rounded-md hover:bg-amber-100 disabled:opacity-50"
                                >
                                    <RotateCcw size={16}/> {retryingReversal ? 'Retrying…' : 'Retry QuickBooks reversal'}
                                </button>
                            </div>
                            {reversalRetryMessage && (
                                <p className={`text-sm mt-2 ${reversalRetryError ? 'text-red-600' : 'text-green-700'}`}>{reversalRetryMessage}</p>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ───────── APPROVAL DETAILS (audit receipt) ─────────
                r1-p7-qdv-approval-section-9d4a7c63 — Phase 7.1 + 7.5 operator surface.
                Expandable, only when an approval doc was fetched. Read-only;
                the audit record is immutable by design. Signature renders to
                match the PDF: drawn → inline SVG, typed → navy cursive.
                IP is operator-only (never shown on the customer portal). */}
            {approval && (
                <div className="max-w-4xl mx-auto mb-4">
                    <div className="rounded-lg border border-gray-200 bg-gray-50 overflow-hidden">
                        <button
                            onClick={() => setApprovalExpanded(v => !v)}
                            className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-100 transition-colors"
                        >
                            <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                                <Fingerprint size={16} className="text-gray-500" />
                                Approval Details
                            </span>
                            {approvalExpanded
                                ? <ChevronUp size={16} className="text-gray-400" />
                                : <ChevronDown size={16} className="text-gray-400" />}
                        </button>

                        {approvalExpanded && (
                            <div className="px-4 pb-4 pt-1 border-t border-gray-200 space-y-3">
                                {/* Signed by + email */}
                                <div>
                                    <p className="text-xs font-bold text-gray-500 uppercase mb-0.5">Approved by</p>
                                    <p className="text-sm text-gray-900">{approval.approvedByName || '—'}</p>
                                    {approval.approvedByEmail && (
                                        <p className="text-xs text-gray-500">{approval.approvedByEmail}</p>
                                    )}
                                </div>

                                {/* Approved on */}
                                {approval.approvedAtMs != null && (
                                    <div>
                                        <p className="text-xs font-bold text-gray-500 uppercase mb-0.5">Approved on</p>
                                        <p className="text-sm text-gray-900">
                                            {new Date(approval.approvedAtMs).toLocaleString(undefined, {
                                                year: 'numeric', month: 'short', day: 'numeric',
                                                hour: 'numeric', minute: '2-digit'
                                            })}
                                        </p>
                                    </div>
                                )}

                                {/* Electronic signature — matches the PDF rendering */}
                                {approval.signatureValue && (
                                    <div>
                                        <p className="text-xs font-bold text-gray-500 uppercase mb-1">Electronic signature</p>
                                        <div className="bg-white border border-gray-200 rounded px-3 py-2 inline-block">
                                            {approval.signatureType === 'drawn' ? (
                                                // r1-p7fix-drawnrender-7c2e9b54 — render drawn signature as
                                                // inline SVG path, matching the PDF generator exactly (raw
                                                // path data can't be shown via <img src>). viewBox 300x100,
                                                // navy stroke, same as functions/index.js signatureHtml.
                                                <svg
                                                    viewBox="0 0 300 100"
                                                    width="240"
                                                    height="80"
                                                    preserveAspectRatio="xMidYMid meet"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d={approval.signatureValue}
                                                        fill="none"
                                                        stroke="#12192D"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            ) : (
                                                <span style={{
                                                    fontFamily: "'Brush Script MT', 'Segoe Script', cursive",
                                                    fontSize: 28,
                                                    color: '#12192D'
                                                }}>
                                                    {approval.signatureValue}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Duration + device + IP (operator-only) */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {approval.approvalDurationMs != null && (
                                        <div>
                                            <p className="text-xs font-bold text-gray-500 uppercase mb-0.5">Time reviewing</p>
                                            <p className="text-sm text-gray-900">
                                                Customer spent {formatApprovalDuration(approval.approvalDurationMs)} reviewing
                                            </p>
                                        </div>
                                    )}
                                    {approval.userAgentCompact && (
                                        <div>
                                            <p className="text-xs font-bold text-gray-500 uppercase mb-0.5">Device</p>
                                            <p className="text-sm text-gray-900">{approval.userAgentCompact}</p>
                                        </div>
                                    )}
                                    {approval.ipAddress && (
                                        <div>
                                            <p className="text-xs font-bold text-gray-500 uppercase mb-0.5">IP address</p>
                                            <p className="text-sm text-gray-900 font-mono">{approval.ipAddress}</p>
                                        </div>
                                    )}
                                    {approval.referenceNumber && (
                                        <div>
                                            <p className="text-xs font-bold text-gray-500 uppercase mb-0.5">Reference #</p>
                                            <p className="text-sm text-gray-900 font-mono">{approval.referenceNumber}</p>
                                        </div>
                                    )}
                                    {/* s8audit-7c2e5b94 — S8b P1: which portal login link the customer used. Approvals from before S8b have no trace (null) → hidden. */}
                                    {approval.portalLink && (
                                        <div>
                                            <p className="text-xs font-bold text-gray-500 uppercase mb-0.5">Portal link</p>
                                            <p className="text-sm text-gray-900">
                                                {!approval.portalLink.tracked
                                                    ? 'Not recorded (link opened before link tracking)'
                                                    : approval.portalLink.automated
                                                        ? 'From an automatic email'
                                                        : `Sent or created by ${approval.portalLink.createdByName || 'a staff member'}`}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* s8audit-7c2e5b94 — S8b P2: staff were signed in on the browser that approved. Honest-only signal, not proof. */}
                                {approval.staffPreviewBrowser === true && (
                                    <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-md p-3">
                                        This approval came from a browser where a staff member had been signed in to Spatchy.
                                        Check that the customer approved it themselves.
                                    </div>
                                )}

                                {/* Customer note */}
                                {approval.customerNote && (
                                    <div>
                                        <p className="text-xs font-bold text-gray-500 uppercase mb-1">Customer note</p>
                                        <p className="text-sm text-gray-900 italic whitespace-pre-wrap">"{approval.customerNote}"</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* r1-1a-notes-strip-9b2e7c58 — Seam 1a: collapsed Notes & Media strip.
                Sits between the action row and the quote document card, collapsed
                by default. Flagging is out of scope (allowFlagging={false}); the
                visibility toggle stays on. NoteCard is pure-presentation here. */}
            <div ref={notesStripRef} className="max-w-4xl mx-auto mb-4">
                <button
                    type="button"
                    onClick={() => setNotesExpanded(prev => !prev)}
                    className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-white rounded-lg shadow-sm border border-gray-100 hover:bg-gray-50"
                >
                    <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                        <MessageSquare size={16} /> Notes &amp; Media
                        <span className="text-xs font-normal text-gray-400">({quoteNotes.length})</span>
                    </span>
                    {notesExpanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
                </button>
                {notesExpanded && (
                    <div className="mt-2 bg-white rounded-lg shadow-sm border border-gray-100 p-4 space-y-3">
                        <NoteComposer
                            attachedTo={[{ type: 'quote', id: quote.id }]}
                            orgId={orgId}
                            currentUserProfile={currentUserProfile}
                            allowFlagging={false}
                            onDirtyChange={setComposerDirty}
                            placeholder="Add a note about this quote..."
                        />
                        <div className="space-y-2 pt-1">
                            {quoteNotes.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-4">No notes yet — add the first one above.</p>
                            ) : (
                                quoteNotes.map(note => ( // r1-2e-qdv-notecard-5d1c8a44
                                    <NoteCard key={note.id} note={note} currentUserProfile={currentUserProfile} orgId={orgId} />
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* qattn-8c4e1f92-qdv-mount — Seam 8: what needs a human, and why.
                Mounted FIRST of the four internal strips deliberately: it is the
                summary the other three explain, and an operator who opens an
                estimate to decide "do I need to do something here" should not
                have to assemble that from a stage, a date and a timeline.
                Reads only props already in scope — no new listener. */}
            {!isDeleted && ( // estdel-f9ac8ba8 — no attention, close, stage or follow-up actions on a deleted estimate
            <QuoteAttentionStrip
                quote={quote}
                pipelineStages={pipelineStages}
                companySettings={companySettings}
                docWord={docWord}
            />
            )}

            {/* qclose-3e8a5c17-qdv-mount — Seam 9b: why it closed. The nudge
                when the server recorded a close nobody has explained yet
                (portal approve/decline, conversion, a phone status chip), or a
                one-line summary once answered. Operator-only, outside the
                document card like the strips around it. */}
            {orgId && !isDeleted && (
                <QuoteCloseStrip
                    quote={quote}
                    record={closeRecord}
                    canAnswer={canAnswerClose}
                    onAnswer={() => openClosePrompt(closeRecord?.closeActivityId || null, closeRecord?.outcome || null)}
                    docWord={docWord}
                />
            )}

            {/* qview-6b2d4e91-qdv-mount — Seam 10: did the customer open it.
                One line, only once there is a counted portal view. Staff only
                (the record is unreadable to the portal). Same centred card as
                the pipeline strip below it, outside the document card. */}
            {viewSummary && (
                <div className="max-w-4xl mx-auto mb-4 px-4 py-3 bg-white rounded-lg shadow-sm border border-gray-100" data-quote-view-line="">
                    <p className="flex items-center gap-2 text-sm text-gray-700">
                        <Eye size={16} className="shrink-0 text-gray-400" />
                        <span>{viewSummary.full}</span>
                    </p>
                </div>
            )}

            {/* qpipe-4d1a7c35-qdv-mount — Seam 4: internal pipeline stage strip.
                Operator-only, gated on orgId like the timeline, and kept OUTSIDE the
                document card below (which mirrors the customer PDF). */}
            {!isDeleted && ( // estdel-f9ac8ba8
            <QuotePipelineStagePicker
                orgId={orgId}
                quote={quote}
                stages={pipelineStages}
                currentUserProfile={currentUserProfile}
                onClosed={(closeActivityId, outcome) => openClosePrompt(closeActivityId, outcome)}
            />
            )}

            {/* qfup-7d3e9a41-qdv-mount — Seam 6: internal follow-up strip (count,
                next date, and the Log follow-up button). Operator-only, same
                gating as the pipeline strip, kept OUTSIDE the document card. */}
            {!isDeleted && ( // estdel-f9ac8ba8
            <QuoteFollowUpStrip
                orgId={orgId}
                quote={quote}
                vocabularies={quoteVocabularies}
                currentUserProfile={currentUserProfile}
                docWord={docWord}
                entries={activityEntries}
                loadError={activityLoadError}
                companySettings={companySettings}
            />
            )}

            {/* qact-3b7e9d52-qdv-mount — Seam 3: minimal Activity timeline, collapsed
                by default, directly after Notes & Media. Operator-only surface. */}
            <QuoteActivityTimeline
                orgId={orgId}
                quote={quote}
                quoteNotes={quoteNotes}
                notesLoading={quoteNotesLoading}
                onOpenNotes={openNotesStrip}
                pipelineStages={pipelineStages}
                entries={activityEntries}
                loadError={activityLoadError}
                closeRecord={closeRecord}
            />

            <CloseDetailsModal
                isOpen={!!closePrompt}
                onClose={() => setClosePrompt(null)}
                quote={quote}
                outcome={closePrompt?.outcome || null}
                closeActivityId={closePrompt?.closeActivityId || null}
                record={closeRecord && !closeRecord.reopenedAt ? closeRecord : null}
                vocabularies={quoteVocabularies}
                docWord={docWord}
            />

            <div className="bg-white p-4 sm:p-8 rounded-lg shadow-lg max-w-4xl mx-auto border border-gray-100">
                <div className="flex flex-col md:flex-row justify-between items-start mb-8 gap-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800">{companySettings?.companyName || 'Your Company'}</h1>
                        <p className="text-gray-500">{companySettings?.address1}</p>
                        <p className="text-gray-500">{`${companySettings?.city || ''}, ${companySettings?.state || ''} ${companySettings?.zip || ''}`}</p>
                        <p className="text-gray-500">{companySettings?.phone}</p>
                        <p className="text-gray-500">{companySettings?.email}</p>
                    </div>
                    <div className="text-left md:text-right w-full md:w-auto">
                        <h2 className="text-4xl font-bold text-gray-200 uppercase">{docWord}</h2>
                        {/* Status badge — Phase 4.5 addition */}
                        <div className="mt-2 mb-2 flex md:justify-end">
                            {sent && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 border border-blue-200">
                                    Sent
                                </span>
                            )}
                            {approved && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200">
                                    <Check size={11} /> {isPartialApproval ? 'Partially Approved' : 'Approved'}
                                </span>
                            )}
                            {/* CONVERT-FIX: r1-cvt-qdv-headerpill-6c1d4e92 — shown alongside the status pill */}
                            {convertedJobId && (
                                <span className="ml-1.5 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-200">
                                    <Briefcase size={11} /> Converted
                                </span>
                            )}
                            {declined && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-200">
                                    <X size={11} /> Declined
                                </span>
                            )}
                            {changesRequested && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                                    <MessageSquare size={11} /> Changes Requested
                                </span>
                            )}
                            {/* qexp-6f2a9d38-qdv — slate, not the old yellow: the estimate
                                wasn't rejected, it ran out of time. Red stays Declined. */}
                            {expired && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                                    <Clock size={11} /> Expired
                                </span>
                            )}
                            {expiringSoon && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                                    <Clock size={11} /> {expiry.daysLeft === 0 ? 'Expires today' : `Expires in ${expiry.daysLeft} day${expiry.daysLeft === 1 ? '' : 's'}`}
                                </span>
                            )}
                        </div>
                        <div className="space-y-1">
                            <p><strong>{docWord} #:</strong> {quote.quoteNumber}</p>
                            <p><strong>Date:</strong> {formatDate(toJsDate(quote.createdAt))}</p>
                            {/* qvalid-qdv-legacyexpiry-4b8e2f91 — a legacy estimate saved without
                                expiresAt must render NO expiry line: toJsDate(undefined) → null and
                                formatDate(null) → new Date(null) → the Unix epoch ("12/31/1969"). */}
                            {quote.expiresAt && (
                                <p><strong>Expires:</strong> {formatDate(toJsDate(quote.expiresAt))}</p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="mb-8">
                    <h3 className="font-semibold text-gray-500 uppercase mb-2 text-xs tracking-wider">Bill To</h3>
                    <div className="text-sm text-gray-800 not-italic space-y-0.5">
                        {customer?.name && <p className="font-bold text-base">{customer.name}</p>}
                        {customer?.email && <p>{customer.email}</p>}
                        {customer?.address1 && <p>{customer.address1}</p>}
                        <p>
                            {customer?.city}{customer?.state && `, ${customer.state}`}{customer?.zip && ` ${customer.zip}`}
                        </p>
                        {customer?.country && <p>{customer.country}</p>}
                    </div>
                </div>

                {/* PROPERTIES v3 — Service location (only when the quote is tied to a property) */}
                {quote.propertyId && (
                    <div className="mb-8">
                        <h3 className="font-semibold text-gray-500 uppercase mb-2 text-xs tracking-wider">Service Location</h3>
                        <div className="text-sm text-gray-800 not-italic space-y-0.5">
                            <p className="font-bold text-base">{quote.propertyName || 'Property'}</p>
                            {quote.serviceAddress && <p className="whitespace-pre-line">{quote.serviceAddress}</p>}
                        </div>
                    </div>
                )}

                {/* Line Items Table — linetext-4d8b2e61: the PDF's layout (name, description under it, one column); fits a phone */}
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                    <table className="w-full mb-8">
                        <thead>
                            <tr className="bg-gray-50 text-left text-gray-600 uppercase text-xs">
                                <th className="py-3 px-3 sm:px-4">Item / Description</th>
                                <th className="hidden sm:table-cell py-3 px-4 text-right whitespace-nowrap">Qty</th>
                                <th className="hidden sm:table-cell py-3 px-4 text-right whitespace-nowrap">Price</th>
                                <th className="py-3 px-3 sm:px-4 text-right whitespace-nowrap">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orderedItems.map((item, index) => {
                                const isIncluded = item.selectionType === SELECTION_TYPES.INCLUDED;
                                const isRequired = item.selectionType === SELECTION_TYPES.REQUIRED;
                                const isRecommended = item.selectionType === SELECTION_TYPES.RECOMMENDED;
                                const isAddon = item.selectionType === SELECTION_TYPES.ADDON;
                                const isDeclined = item.itemId && declinedSet.has(item.itemId);

                                return (
                                    <tr
                                        key={item.itemId || index}
                                        className={`border-b border-gray-100 last:border-0 ${isDeclined ? 'opacity-50' : ''}`}
                                    >
                                        <td className={`py-4 px-3 sm:px-4 align-top break-words ${LINE_NAME}`}>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                {/* Selection-type icon indicator */}
                                                {isIncluded && <Link2 size={12} className="text-purple-400 flex-shrink-0" />}
                                                <span className={`${isIncluded ? 'pl-2' : ''} ${isDeclined ? 'line-through' : ''}`}>
                                                    {item.name}
                                                </span>
                                                {/* Selection type badge — only show when not Required (Required is default/expected) */}
                                                {isRecommended && (
                                                    <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 px-1 py-0.5 rounded">
                                                        <Star size={8} /> Rec
                                                    </span>
                                                )}
                                                {isAddon && (
                                                    <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-100 px-1 py-0.5 rounded">
                                                        <Plus size={8} /> Optional
                                                    </span>
                                                )}
                                                {isIncluded && (
                                                    <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wide text-purple-700 bg-purple-100 px-1 py-0.5 rounded">
                                                        Included
                                                    </span>
                                                )}
                                                {isDeclined && (
                                                    <span className="inline-flex items-center text-[9px] font-bold uppercase tracking-wide text-red-700 bg-red-100 px-1 py-0.5 rounded">
                                                        Declined
                                                    </span>
                                                )}
                                            </div>
                                            {item.description && (
                                                <span className={`${LINE_DESC} ${isIncluded ? 'pl-2' : ''} ${isDeclined ? 'line-through' : ''}`}>
                                                    <LineDescription text={item.description} />
                                                </span>
                                            )}
                                            {item.taxExempt && (
                                                <p className="text-[10px] text-gray-400 italic font-normal mt-1">
                                                    (Tax Exempt #: {item.taxExemptionNumber || 'N/A'})
                                                </p>
                                            )}
                                            {/* a phone hides the Qty and Price columns: they show here instead */}
                                            <span className={`sm:hidden block mt-1 text-xs font-normal text-gray-500 ${isDeclined ? 'line-through' : ''}`}>
                                                {item.quantity} × {formatCurrency(item.price, currencyCode)}
                                            </span>
                                        </td>
                                        <td className={`hidden sm:table-cell py-4 px-4 text-right text-gray-600 ${LINE_NUM} ${isDeclined ? 'line-through' : ''}`}>
                                            {item.quantity}
                                        </td>
                                        <td className={`hidden sm:table-cell py-4 px-4 text-right text-gray-600 ${LINE_NUM} ${isDeclined ? 'line-through' : ''}`}>
                                            {formatCurrency(item.price, currencyCode)}
                                        </td>
                                        <td className={`py-4 px-3 sm:px-4 text-right font-semibold text-gray-900 ${LINE_NUM} ${isDeclined ? 'line-through' : ''}`}>
                                            {formatCurrency(Number(item.quantity || 1) * Number(item.price || 0), currencyCode)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* ── Totals: show approved vs. original when partial approval ── */}
                <div className="flex justify-end mt-4">
                    <div className="w-full max-w-sm space-y-2">
                        {isPartialApproval && approvedTotal != null ? (
                            <>
                                {/* Original totals as a smaller dimmed block */}
                                <div className="pb-3 border-b border-gray-200 space-y-1">
                                    <div className="flex justify-between text-xs text-gray-400">
                                        <span>Originally quoted:</span>
                                        <span>{formatCurrency(originalTotal, currencyCode)}</span>
                                    </div>
                                </div>
                                {/* Approved totals as the primary display */}
                                <div className="flex justify-between text-sm pt-2">
                                    <span className="text-gray-500">Approved subtotal:</span>
                                    <span className="font-medium text-gray-900">{formatCurrency(approvedSubtotal ?? 0, currencyCode)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-500">
                                        Tax {approvedSubtotal > 0 ? `(${((approvedTaxAmount / approvedSubtotal) * 100).toFixed(1)}%)` : ''}:
                                    </span>
                                    <span className="font-medium text-gray-900">{formatCurrency(approvedTaxAmount ?? 0, currencyCode)}</span>
                                </div>
                                <div className="flex justify-between py-3 border-t border-gray-200 mt-2">
                                    <span className="text-lg font-bold text-gray-900">Approved total ({currencyCode}):</span>
                                    <span className="text-2xl font-black text-green-600">{formatCurrency(approvedTotal, currencyCode)}</span>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-500">Subtotal:</span>
                                    <span className="font-medium text-gray-900">{formatCurrency(originalSubtotal, currencyCode)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-500">
                                        Tax {originalSubtotal > 0 ? `(${((originalTaxAmount / originalSubtotal) * 100).toFixed(1)}%)` : ''}:
                                    </span>
                                    <span className="font-medium text-gray-900">{formatCurrency(originalTaxAmount, currencyCode)}</span>
                                </div>
                                <div className="flex justify-between py-3 border-t border-gray-200 mt-2">
                                    <span className="text-lg font-bold text-gray-900">Total ({currencyCode}):</span>
                                    <span className={`text-2xl font-black ${approved ? 'text-green-600' : 'text-blue-600'}`}>
                                        {formatCurrency(originalTotal, currencyCode)}
                                    </span>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* doced-8f3a61c2 — Stage 5e: the estimate's customer-facing Terms (quote.terms), where the PDF puts
                    them (after the totals). Only when there are terms; React escapes the text; line breaks kept. */}
                {typeof quote.terms === 'string' && quote.terms.trim() !== '' && (
                    <div className="mt-10 pt-6 border-t border-gray-100">
                        <h4 className="font-semibold text-xs uppercase text-gray-400 mb-2 tracking-wide">Terms</h4>
                        <p className="text-gray-700 text-sm whitespace-pre-wrap break-words">{quote.terms.trim()}</p>
                    </div>
                )}

                {/* doced-8f3a61c2 — quote.notes is the INTERNAL notes field (staff only; never on the PDF, email or
                    portal). Its old heading put "notes" and "terms" together, which read as if customers saw it. */}
                {quote.notes && (
                    <div className="mt-8 p-4 bg-gray-50 border border-dashed border-gray-300 rounded-lg" aria-label="Internal notes">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                            <Lock size={13} className="text-gray-400" />
                            <h4 className="font-semibold text-xs uppercase text-gray-500 tracking-wide">Internal notes</h4>
                            <span className="text-xs text-gray-400">(not shown to the customer)</span>
                            <span className="ml-auto text-xs text-gray-400">Internal: customers never see this</span>
                        </div>
                        <p className="text-gray-600 text-sm whitespace-pre-wrap break-words">{quote.notes}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

// taxexempt3-8c4f2a1d — after "Charge tax on this document" / "Apply exemption again" the page shows the
// document as the server now has it (TaxExemptionBanner re-reads it), without closing the page.
export default withExemptionRefresh(QuoteDetailsView, 'quote');